import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, Trash2, Check, X, Calendar, MapPin, Clock, ArrowUpRight } from "lucide-react";
import { supabase, formatSupabaseError } from "../lib/supabase";

const STATUS_LABELS = {
  pending: "Anfrage",
  confirmed: "Bestätigt",
  declined: "Abgelehnt",
  completed: "Erledigt",
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      weekday: "short",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

export const BookingsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("studio_bookings")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) toast.error(formatSupabaseError(error));
    else setItems(data || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id, status) => {
    const { error } = await supabase.from("studio_bookings").update({ status }).eq("id", id);
    if (error) return toast.error(formatSupabaseError(error));
    setItems((arr) => arr.map((it) => (it.id === id ? { ...it, status } : it)));
    if (selected?.id === id) setSelected((s) => ({ ...s, status }));
    toast.success("Status aktualisiert.");
  };

  const remove = async (id) => {
    if (!window.confirm("Buchung wirklich löschen?")) return;
    const { error } = await supabase.from("studio_bookings").delete().eq("id", id);
    if (error) return toast.error(formatSupabaseError(error));
    setItems((arr) => arr.filter((it) => it.id !== id));
    if (selected?.id === id) setSelected(null);
    toast.success("Gelöscht.");
  };

  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);
  const counts = {
    all: items.length,
    pending: items.filter((i) => i.status === "pending").length,
    confirmed: items.filter((i) => i.status === "confirmed").length,
    declined: items.filter((i) => i.status === "declined").length,
    completed: items.filter((i) => i.status === "completed").length,
  };

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="flex items-center gap-3 text-[11px] tracking-[0.4em] uppercase text-zinc-500 font-semibold mb-4">
            <span className="red-led" /> Studio Buchungen
          </div>
          <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-4xl md:text-5xl">Sessions</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="bookings-filters">
          {[
            ["all", "Alle"],
            ["pending", "Anfrage"],
            ["confirmed", "Bestätigt"],
            ["declined", "Abgelehnt"],
            ["completed", "Erledigt"],
          ].map(([val, label]) => (
            <button
              key={val}
              onClick={() => setFilter(val)}
              className={`text-[10px] tracking-[0.25em] uppercase px-3 py-2 border transition-colors ${
                filter === val ? "border-[#ff0033]/60 text-white bg-[#ff0033]/10" : "border-white/10 text-zinc-500 hover:text-white"
              }`}
            >
              {label} · {counts[val]}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="text-zinc-500 text-sm">Lade…</div>}

      {!loading && filtered.length === 0 && (
        <div className="border border-dashed border-white/[0.08] bg-[#080808] py-20 text-center">
          <div className="red-led mx-auto mb-4" />
          <div className="text-sm text-zinc-500">Keine Buchungen in dieser Ansicht.</div>
        </div>
      )}

      <div className="grid gap-3">
        {filtered.map((b) => (
          <article
            key={b.id}
            data-testid={`booking-${b.id}`}
            className="bg-[#0A0A0A] border border-white/[0.06] p-6 hover:border-[#ff0033]/30 transition-colors"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3 flex-wrap mb-2">
                  <StatusBadge status={b.status} />
                  <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-600">
                    Eingang: {new Date(b.created_at).toLocaleString("de-DE")}
                  </span>
                </div>
                <div className="font-display font-bold text-xl uppercase tracking-tight">
                  {b.name}
                  {b.artist_name && <span className="text-[#ff0033] ml-3">· {b.artist_name}</span>}
                </div>
                <div className="mt-2 grid md:grid-cols-3 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-zinc-300">
                    <Calendar size={12} className="text-[#ff0033]" />
                    <span>{formatDate(b.preferred_date)}{b.preferred_time ? ` · ${b.preferred_time}` : ""}</span>
                  </div>
                  <div className="flex items-center gap-2 text-zinc-300">
                    <MapPin size={12} className="text-[#ff0033]" />
                    <span className="truncate">{b.location}</span>
                  </div>
                  <div className="flex items-center gap-2 text-zinc-300">
                    <Clock size={12} className="text-[#ff0033]" />
                    <span>{(b.session_type || "—").replace(/-/g, " ")}{b.duration ? ` · ${b.duration}` : ""}</span>
                  </div>
                </div>
                <div className="mt-3 text-sm text-zinc-400 flex items-center gap-4 flex-wrap">
                  <a href={`mailto:${b.email}`} className="hover:text-white">{b.email}</a>
                  {b.phone && <a href={`tel:${b.phone}`} className="hover:text-white">{b.phone}</a>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0 flex-wrap">
                <button onClick={() => setSelected(b)} data-testid={`booking-view-${b.id}`} className="p-2.5 border border-white/10 hover:border-[#ff0033]/50 hover:text-[#ff0033] text-zinc-400 transition-colors" title="Details">
                  <Eye size={14} />
                </button>
                {b.status === "pending" && (
                  <>
                    <button onClick={() => setStatus(b.id, "confirmed")} data-testid={`booking-confirm-${b.id}`} className="p-2.5 border border-white/10 hover:border-[#ff0033]/50 text-zinc-400 hover:text-[#ff0033] transition-colors" title="Bestätigen">
                      <Check size={14} />
                    </button>
                    <button onClick={() => setStatus(b.id, "declined")} className="p-2.5 border border-white/10 hover:border-red-500/50 text-zinc-400 hover:text-red-500 transition-colors" title="Ablehnen">
                      <X size={14} />
                    </button>
                  </>
                )}
                <button onClick={() => remove(b.id)} data-testid={`booking-delete-${b.id}`} className="p-2.5 border border-white/10 hover:border-red-500/50 hover:text-red-500 text-zinc-400 transition-colors" title="Löschen">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={() => setSelected(null)}>
          <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-2xl bg-[#0A0A0A] border border-white/[0.08] p-8 md:p-10 my-10" data-testid="booking-detail">
            <div className="red-led absolute top-5 right-5" />
            <div className="flex items-center gap-3 mb-3">
              <StatusBadge status={selected.status} />
              <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
                {(selected.session_type || "—").replace(/-/g, " ")}
              </span>
            </div>
            <h3 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">
              {selected.name}
              {selected.artist_name && <span className="text-zinc-500 block text-xl mt-1">/ {selected.artist_name}</span>}
            </h3>
            <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
              <Info label="E-Mail" value={<a href={`mailto:${selected.email}`} className="hover:text-[#ff0033]">{selected.email}</a>} />
              <Info label="Telefon" value={selected.phone ? <a href={`tel:${selected.phone}`} className="hover:text-[#ff0033]">{selected.phone}</a> : "—"} />
              <Info label="Datum" value={formatDate(selected.preferred_date)} />
              <Info label="Uhrzeit" value={selected.preferred_time || "—"} />
              <Info label="Ort" value={selected.location} />
              <Info label="Dauer" value={selected.duration || "—"} />
            </div>
            {selected.message && (
              <div className="mt-8 border-t border-white/10 pt-6">
                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-3">Vision / Anmerkungen</div>
                <p className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap">{selected.message}</p>
              </div>
            )}
            <div className="mt-8 flex justify-end gap-2 flex-wrap">
              {selected.status === "pending" && (
                <>
                  <button onClick={() => setStatus(selected.id, "declined")} className="text-[10px] tracking-[0.3em] uppercase border border-white/15 px-4 py-2.5 hover:border-red-500/50 hover:text-red-500">
                    Ablehnen
                  </button>
                  <button onClick={() => setStatus(selected.id, "confirmed")} className="text-[10px] tracking-[0.3em] uppercase border border-[#ff0033]/40 bg-[#ff0033]/10 text-white px-4 py-2.5 hover:bg-[#ff0033]/20">
                    Bestätigen
                  </button>
                </>
              )}
              {selected.status === "confirmed" && (
                <button onClick={() => setStatus(selected.id, "completed")} className="text-[10px] tracking-[0.3em] uppercase border border-white/15 px-4 py-2.5 hover:border-[#ff0033]/50">
                  Als erledigt markieren
                </button>
              )}
              <button onClick={() => setSelected(null)} className="text-[10px] tracking-[0.3em] uppercase border border-white/15 px-4 py-2.5 hover:border-white/30">
                Schließen
              </button>
              <a
                href={`mailto:${selected.email}?subject=${encodeURIComponent(
                  `Deine Studio-Anfrage bei STRAßENWERT — ${formatDate(selected.preferred_date)}`
                )}&body=${encodeURIComponent(
                  `Hi ${selected.name},\n\nvielen Dank für deine Anfrage am ${formatDate(selected.preferred_date)} in ${selected.location}.\n\n`
                )}`}
                className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-bold bg-white text-black px-5 py-2.5 hover:shadow-[0_0_20px_rgba(255,0,51,0.3)] transition-shadow"
              >
                Per E-Mail antworten <ArrowUpRight size={12} />
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const style = {
    pending: "border-[#ff0033]/50 text-[#ff0033] bg-[#ff0033]/10",
    confirmed: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
    declined: "border-white/15 text-zinc-500",
    completed: "border-white/15 text-zinc-400",
  }[status] || "border-white/15 text-zinc-400";
  return (
    <span className={`text-[10px] tracking-[0.3em] uppercase font-bold px-2 py-1 border ${style}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
};

const Info = ({ label, value }) => (
  <div>
    <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-600 mb-1">{label}</div>
    <div className="text-zinc-200">{value}</div>
  </div>
);

export default BookingsTab;
